/**
 * Smooth Infinite Scroll for Siedres Collection Pages
 *
 * Preloads next page content and smoothly reveals products as user scrolls down.
 */

const INFINITE_SCROLL_DEBUG = false;
function _isLog(...args) { if (INFINITE_SCROLL_DEBUG) console.log(...args); }

class SmoothInfiniteScroll {
  constructor() {
    _isLog('SmoothInfiniteScroll: Constructor started...');

    // Core elements
    this.productGrid = document.querySelector('.grid-outer');
    this.productContainer = document.querySelector('.grid#CollectionLoop');
    this.loadingIndicator = document.querySelector('.grid__loader');

    // Get collection information from section data attributes
    this.collectionSection = document.querySelector('.collection-products');

    // State tracking
    this.isLoading = false;
    this.nextPageUrl = null;
    this.canLoadMore = false;
    this.allProductsLoaded = false;

    // Make this instance globally available for other scripts
    window.infiniteScrollInstance = this;
    this.preloadedItems = [];
    this.preloadThreshold = 5;
    this.itemsPerPage = 0;
    this.currentPage = 1;
    this.totalPages = 1;

    // Grid tracking for media block positioning
    this.gridColumns = 4;
    this.currentRowPosition = 1;

    // Important: Make sure we have only ONE observer
    this.observer = null;

    // Define custom events for other scripts to listen to
    this.customEvents = {
      pageLoaded: new CustomEvent('smooth-infinite-scroll:page-loaded', { bubbles: true }),
      itemsRevealed: new CustomEvent('smooth-infinite-scroll:items-revealed', { bubbles: true })
    };

    // Get current page and total pages from collection section data attributes
    if (this.collectionSection) {
      this.currentPage = parseInt(this.collectionSection.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.collectionSection.dataset.totalPages) || 1;
      this.collectionUrl = this.collectionSection.dataset.collection || '/collections/all';

      _isLog('SmoothInfiniteScroll: Collection info -', {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        collectionUrl: this.collectionUrl
      });
    }

    // Only initialize if we're on a collection page with the required elements
    if (this.productGrid && this.productContainer && this.collectionSection) {
      this.init();
    }
  }

  init() {
    _isLog('SmoothInfiniteScroll: Initializing...');

    // Check if we have more pages to load
    if (this.currentPage < this.totalPages) {
      this.canLoadMore = true;

      // Construct the next page URL preserving current filters/sort
      const currentURL = new URL(window.location.href);
      currentURL.searchParams.set('page', this.currentPage + 1);
      this.nextPageUrl = currentURL.toString();
    } else {
      this.canLoadMore = false;
      this.allProductsLoaded = true;
    }

    // Add a class to the body for styling
    document.body.classList.add('has-infinite-scroll');

    // Configure loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.style.opacity = '0';
      this.loadingIndicator.style.display = 'block';
    }

    // Count initial items to determine items per page
    let initialItems = this.productContainer.querySelectorAll('.grid-item.product-item');
    if (initialItems.length === 0) {
      initialItems = this.productContainer.querySelectorAll('[data-grid-item]');
    }
    if (initialItems.length === 0) {
      initialItems = this.productContainer.querySelectorAll(':scope > *');
    }

    this.itemsPerPage = initialItems.length;
    _isLog('SmoothInfiniteScroll: Found', this.itemsPerPage, 'initial items');

    if (this.itemsPerPage === 0) {
      setTimeout(() => {
        const retryItems = this.productContainer.querySelectorAll('.grid-item.product-item, [data-grid-item], :scope > *');
        this.itemsPerPage = retryItems.length;
        this.setupIntersectionObserver();
      }, 1000);
    } else {
      this.setupIntersectionObserver();
    }

    // Start preloading the next page immediately if we can load more
    if (this.canLoadMore) {
      this.preloadNextPage();
    }
  }

  setupIntersectionObserver() {
    try {
      // Clean up existing observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Debounce variable
      this._observerTimeout = null;

      // Responsive rootMargin: smaller on mobile for better performance
      var rootMarginValue = window.innerWidth < 768 ? '200px 0px' : '500px 0px';

      // Create the observer
      this.observer = new IntersectionObserver((entries) => {
        if (this._observerTimeout) clearTimeout(this._observerTimeout);

        this._observerTimeout = setTimeout(() => {
          const hasIntersectingEntry = entries.some(entry => entry.isIntersecting);

          if (hasIntersectingEntry) {
            this.revealPreloadedItems();

            if (this.canLoadMore && !this.isLoading) {
              this.preloadNextPage();
            }
          }
        }, 50);
      }, {
        rootMargin: rootMarginValue,
        threshold: 0.05
      });

      // Observe the trigger items
      this.updateObservedItems();
    } catch (error) {
      console.error('SmoothInfiniteScroll: Error setting up observer:', error);
    }
  }

  updateObservedItems() {
    if (!this.observer) {
      setTimeout(() => {
        this.setupIntersectionObserver();
      }, 500);
      return;
    }

    try {
      this.observer.disconnect();
    } catch (error) {
      // Ignore disconnect errors
    }

    // Helper function to find grid items
    const getGridItems = (container) => {
      let items = container.querySelectorAll('.grid-item.product-item:not(.product-item--featured-image)');
      if (items.length === 0) items = container.querySelectorAll('[data-grid-item]');
      if (items.length === 0) items = container.querySelectorAll(':scope > *');
      return items;
    };

    const allItems = getGridItems(this.productContainer);

    if (allItems.length === 0) {
      setTimeout(() => this.updateObservedItems(), 1000);
      return;
    }

    // Observe last N items as triggers
    const startIndex = Math.max(0, allItems.length - this.preloadThreshold);

    for (let i = startIndex; i < allItems.length; i++) {
      if (allItems[i] instanceof Element) {
        this.observer.observe(allItems[i]);
      }
    }
  }

  async preloadNextPage() {
    if (!this.nextPageUrl || this.isLoading || this.allProductsLoaded) {
      return;
    }

    this.isLoading = true;

    if (this.loadingIndicator) {
      this.loadingIndicator.style.opacity = '0.5';
    }

    try {
      this._abortController = new AbortController();
      const timeoutId = setTimeout(() => this._abortController.abort(), 15000);
      const response = await fetch(this.nextPageUrl, { signal: this._abortController.signal });
      clearTimeout(timeoutId);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      let newProductItems = doc.querySelectorAll('.grid#CollectionLoop .grid-item.product-item');

      if (newProductItems.length === 0) {
        newProductItems = doc.querySelectorAll('.grid#CollectionLoop [data-grid-item]');
      }

      if (newProductItems.length === 0) {
        const collectionLoop = doc.querySelector('.grid#CollectionLoop');
        if (collectionLoop) {
          newProductItems = collectionLoop.querySelectorAll(':scope > *');
        }
      }

      if (newProductItems.length > 0) {
        const actualProductNodes = Array.from(newProductItems);

        // Clear previous preload cache before adding new items
        this.preloadedItems = [];

        actualProductNodes.forEach((item) => {
          const clonedItem = item.cloneNode(true);
          clonedItem.classList.add('infinite-scroll-item');
          this.preloadedItems.push(clonedItem);
        });

        // Update current page
        this.currentPage++;

        // Dispatch page changed event
        document.dispatchEvent(new CustomEvent('infinite-scroll:page-changed', {
          detail: { page: this.currentPage, itemsPerPage: this.itemsPerPage }
        }));

        // Check if we've reached the last page
        if (this.currentPage >= this.totalPages) {
          this.nextPageUrl = null;
          this.allProductsLoaded = true;
        } else {
          const currentURL = new URL(window.location.href);
          currentURL.searchParams.set('page', this.currentPage + 1);
          this.nextPageUrl = currentURL.toString();

          document.dispatchEvent(this.customEvents.pageLoaded);
        }

        // Check if we should reveal items based on scroll position
        const scrollY = window.scrollY || window.pageYOffset;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollPercentage = (scrollY + windowHeight) / documentHeight;

        if (scrollPercentage > 0.8) {
          this.revealPreloadedItems();
        }
      } else {
        this.nextPageUrl = null;
        this.allProductsLoaded = true;
      }
    } catch (error) {
      console.error('SmoothInfiniteScroll: Error preloading:', error);
      this.nextPageUrl = null;
      this.allProductsLoaded = true;
    } finally {
      if (this.loadingIndicator) {
        setTimeout(() => {
          this.loadingIndicator.style.opacity = '0';
        }, 300);
      }

      this.isLoading = false;
    }
  }

  revealPreloadedItems() {
    if (this.preloadedItems.length === 0) return;

    _isLog('Revealing', this.preloadedItems.length, 'preloaded items');

    const fragment = document.createDocumentFragment();

    this.preloadedItems.forEach((item, index) => {
      item.classList.add('infinite-scroll-item');
      const delayGroup = Math.floor(index / 4);
      item.style.animationDelay = `${delayGroup * 50}ms`;
      fragment.appendChild(item);
    });

    this.productContainer.appendChild(fragment);

    // Ensure media blocks data is available
    if (window.collectionMediaManager === undefined && typeof window.restoreMediaBlocksData === 'function') {
      window.restoreMediaBlocksData();
    }

    this.initializeNewProducts();
    this.preloadedItems = [];
    this.updateObservedItems();

    document.dispatchEvent(this.customEvents.itemsRevealed);

    // Media block re-initialization
    setTimeout(() => {
      if (typeof window.restoreMediaBlocksData === 'function') {
        window.restoreMediaBlocksData();
      }

      if (window.collectionMediaManager) {
        window.collectionMediaManager.removeAllMediaBlocks();
        window.collectionMediaManager.insertMediaBlocks();
      } else if (typeof CollectionMediaManager === 'function') {
        window.collectionMediaManager = new CollectionMediaManager();
      }

      this.refreshMediaBlockStyles();
    }, 300);

    if (this.allProductsLoaded) {
      document.body.classList.add('all-products-loaded');
    } else {
      if (!this.isLoading && this.nextPageUrl) {
        this.preloadNextPage();
      }
    }
  }

  refreshMediaBlockStyles() {
    const mediaBlocks = document.querySelectorAll('.product-item--featured-image');
    if (mediaBlocks.length === 0) return;

    mediaBlocks.forEach(block => {
      void block.offsetWidth;
      block.style.display = 'block';

      const width = block.getAttribute('data-media-width') || '1';
      const height = block.getAttribute('data-media-height') || '1';
      block.style.gridColumnEnd = `span ${width}`;
      block.style.gridRowEnd = `span ${height}`;

      const images = block.querySelectorAll('img');
      const videos = block.querySelectorAll('video, iframe');

      images.forEach(img => {
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.style.position = 'absolute';
      });

      videos.forEach(video => {
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.position = 'absolute';
      });
    });
  }

  initializeNewProducts() {
    requestAnimationFrame(() => {
      if (typeof window.initProductCarousel === 'function') {
        window.initProductCarousel();
      }

      // Initialize product swiper in batches
      const newSwipers = document.querySelectorAll('.infinite-scroll-item .product-item__carousel');
      if (newSwipers.length > 0 && typeof Swiper !== 'undefined') {
        const initializeSwipers = (startIndex, batchSize) => {
          const endIndex = Math.min(startIndex + batchSize, newSwipers.length);

          for (let i = startIndex; i < endIndex; i++) {
            const swiperEl = newSwipers[i];
            if (!swiperEl.swiper && !swiperEl.classList.contains('swiper-initialized')) {
              new Swiper(swiperEl, {
                slidesPerView: 1,
                spaceBetween: 0,
                watchOverflow: true,
                threshold: 2,
                lazy: true,
                preloadImages: false,
                pagination: {
                  el: swiperEl.querySelector('.swiper-pagination'),
                  clickable: true
                }
              });
            }
          }

          if (endIndex < newSwipers.length) {
            requestIdleCallback ? requestIdleCallback(() => initializeSwipers(endIndex, batchSize)) :
              setTimeout(() => initializeSwipers(endIndex, batchSize), 10);
          }
        };

        initializeSwipers(0, 5);
      }

      // Media manager
      try {
        if (!window.collectionMediaManager && typeof CollectionMediaManager === 'function') {
          window.collectionMediaManager = new CollectionMediaManager();
        } else if (window.collectionMediaManager) {
          window.collectionMediaManager.insertMediaBlocks();
        }
      } catch (e) {
        _isLog('SmoothInfiniteScroll: CollectionMediaManager init error:', e);
      }

      // Initialize hover effects
      const newHoverItems = document.querySelectorAll('.infinite-scroll-item .product-item');
      if (newHoverItems.length > 0) {
        for (let i = 0; i < newHoverItems.length; i++) {
          const links = newHoverItems[i].querySelectorAll('a');
          for (let j = 0; j < links.length; j++) {
            if (!links[j].hasAttribute('data-no-instant')) {
              links[j].setAttribute('data-no-instant', '');
            }
          }
        }
      }

      // Update URL with current page
      if (this.currentPage > 1 && window.history && window.history.pushState) {
        const url = new URL(window.location.href);
        const currentUrlPage = parseInt(url.searchParams.get('page') || '1');

        if (currentUrlPage !== this.currentPage) {
          url.searchParams.set('page', this.currentPage);
          window.history.pushState({ page: this.currentPage }, '', url.toString());
        }
      }
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this._observerTimeout) {
      clearTimeout(this._observerTimeout);
    }
    this.preloadedItems = [];
    this.isLoading = false;
    if (this._abortController) { this._abortController.abort(); this._abortController = null; }
    delete window.infiniteScrollInstance;
    _isLog('SmoothInfiniteScroll: Destroyed');
  }
}

// Initialize smooth infinite scroll when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  const collectionPage = document.querySelector('.collection-products');

  if (collectionPage) {
    window.siedresInfiniteScroll = new SmoothInfiniteScroll();

    if (!window.collectionMediaBlocks && typeof window.restoreMediaBlocksData === 'function') {
      window.restoreMediaBlocksData();
    }

    // Watch for sidebar filter completion (collection-filters-form.js uses is-loading class)
    var productsGrid = document.querySelector('[data-products-grid]');
    if (productsGrid) {
      var wasLoading = false;
      new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName !== 'class') continue;
          var nowLoading = productsGrid.classList.contains('is-loading');
          if (wasLoading && !nowLoading) {
            // Sidebar filter completed — fetch pagination data + reinit infinite scroll
            fetch(window.location.href)
              .then(function(res) { return res.text(); })
              .then(function(html) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var newCollProd = doc.querySelector('.collection-products');
                var curCollProd = document.querySelector('.collection-products');
                if (newCollProd && curCollProd) {
                  curCollProd.setAttribute('data-current-page',
                    newCollProd.getAttribute('data-current-page') || '1');
                  curCollProd.setAttribute('data-total-pages',
                    newCollProd.getAttribute('data-total-pages') || '1');
                }
                if (window.siedresInfiniteScroll) {
                  window.siedresInfiniteScroll.destroy();
                }
                window.siedresInfiniteScroll = new SmoothInfiniteScroll();
              })
              .catch(function() {
                if (window.siedresInfiniteScroll) {
                  window.siedresInfiniteScroll.destroy();
                }
                window.siedresInfiniteScroll = new SmoothInfiniteScroll();
              });
          }
          wasLoading = nowLoading;
        }
      }).observe(productsGrid, { attributes: true, attributeFilter: ['class'] });
    }

    // Handle browser back/forward navigation
    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.page) {
        if (typeof window.restoreMediaBlocksData === 'function') {
          window.restoreMediaBlocksData();
        }
        window.location.reload();
      }
    });
  }
});

// Listen for filter changes from archive-sale-collection.js or sidebar
document.addEventListener('collection:filters-changed', function() {
  if (window.siedresInfiniteScroll) {
    window.siedresInfiniteScroll.destroy();
    window.siedresInfiniteScroll = new SmoothInfiniteScroll();
  }
});
